<template>
  <!-- A bar the listener may not move (`seeking=false`) is still drawn, as a progress bar. -->
  <div
    ref="containerRef"
    class="audio-progress-bar"
    :class="{ 'audio-progress-bar--still': !interactive }"
    :role="interactive ? 'slider' : 'progressbar'"
    aria-label="Audio progress"
    :aria-valuenow="Math.round(progressPercent)"
    :aria-valuemin="0"
    :aria-valuemax="100"
    :aria-valuetext="`${formattedCurrentTime} of ${formattedDuration}`"
    :tabindex="interactive ? 0 : undefined"
    @mousedown="handleMouseDown"
    @touchstart.passive="handleTouchStart"
    @keydown="handleKeyDown"
  >
    <!-- Track background -->
    <div class="audio-progress-bar__track">
      <!-- Buffered range -->
      <div
        v-if="bufferedAhead"
        :key="bufferedAhead.start"
        class="audio-progress-bar__buffered"
        :style="{
          left: `${pctOf(bufferedAhead.start)}%`,
          width: `${Math.max(0, pctOf(bufferedAhead.end) - pctOf(bufferedAhead.start))}%`,
        }"
      />
      <!-- Played range -->
      <div
        class="audio-progress-bar__fill"
        :style="{ width: `${progressPercent}%`, backgroundColor: accentColor }"
      />
      <!-- Hover preview -->
      <div
        v-if="interactive && isHovering && !isSeeking"
        class="audio-progress-bar__hover"
        :style="{ width: `${hoverPercent}%` }"
      />
    </div>

    <!-- Thumb indicator -->
    <div
      class="audio-progress-bar__thumb"
      :style="{ left: `${progressPercent}%`, backgroundColor: accentColor }"
    />

    <!-- Hover time tooltip -->
    <div
      v-if="interactive && (isHovering || isSeeking)"
      class="audio-progress-bar__tooltip"
      :style="{ left: `${isSeeking ? progressPercent : hoverPercent}%` }"
    >
      {{ hoverTimeFormatted }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { formatTime } from '../utils/audio-utils'
import { bufferedAt, type TimeRange } from '../../types/base'

const props = withDefaults(
  defineProps<{
    currentTime: number
    duration: number
    buffered?: { start: number; end: number }[]
    accentColor?: string
    /** Whether the listener may move the playhead from the bar (`seeking`). */
    interactive?: boolean
  }>(),
  {
    buffered: () => [],
    accentColor: undefined,
    interactive: true,
  },
)

const emit = defineEmits<{
  seek: [time: number]
}>()

const containerRef = ref<HTMLElement | null>(null)
const isHovering = ref(false)
const isSeeking = ref(false)
const hoverPercent = ref(0)

/** Map a media time to a 0-100 position on the bar. */
function pctOf(time: number): number {
  if (!props.duration || props.duration <= 0) return 0
  return Math.min(100, Math.max(0, (time / props.duration) * 100))
}

const progressPercent = computed(() => pctOf(props.currentTime))

/**
 * What is ready ahead of the playhead — see [`bufferedAt`] for why it is one range.
 *
 * **This used to be a width alone, measured off the *last* range's end**, so a bar that started
 * at 0 claimed everything up to the furthest-buffered point: seek forward and the hole behind
 * the new range read as ready. It takes a left now, like the video player's.
 */
const bufferedAhead = computed<TimeRange | undefined>(() =>
  bufferedAt(props.buffered, props.currentTime),
)

const formattedCurrentTime = computed(() => formatTime(props.currentTime))
const formattedDuration = computed(() => formatTime(props.duration))

const hoverTimeFormatted = computed(() => {
  const time = ((isSeeking.value ? progressPercent.value : hoverPercent.value) / 100) * props.duration
  return formatTime(Math.max(0, time))
})

function getPercentFromEvent(e: MouseEvent | Touch): number {
  if (!containerRef.value) return 0
  const rect = containerRef.value.getBoundingClientRect()
  const x = ('clientX' in e ? e.clientX : 0) - rect.left
  return Math.max(0, Math.min(100, (x / rect.width) * 100))
}

function seekToPercent(percent: number): void {
  if (!props.duration) return
  emit('seek', (percent / 100) * props.duration)
}

function handleMouseDown(e: MouseEvent): void {
  if (!props.interactive) return
  e.preventDefault()
  isSeeking.value = true
  const percent = getPercentFromEvent(e)
  seekToPercent(percent)

  const handleMouseMove = (ev: MouseEvent) => {
    const p = getPercentFromEvent(ev)
    seekToPercent(p)
  }

  const handleMouseUp = () => {
    isSeeking.value = false
    document.removeEventListener('mousemove', handleMouseMove)
    document.removeEventListener('mouseup', handleMouseUp)
  }

  document.addEventListener('mousemove', handleMouseMove)
  document.addEventListener('mouseup', handleMouseUp)
}

function handleTouchStart(e: TouchEvent): void {
  if (!props.interactive || !e.touches.length) return
  isSeeking.value = true
  const percent = getPercentFromEvent(e.touches[0])
  seekToPercent(percent)

  const handleTouchMove = (ev: TouchEvent) => {
    if (!ev.touches.length) return
    seekToPercent(getPercentFromEvent(ev.touches[0]))
  }

  const handleTouchEnd = () => {
    isSeeking.value = false
    document.removeEventListener('touchmove', handleTouchMove)
    document.removeEventListener('touchend', handleTouchEnd)
  }

  document.addEventListener('touchmove', handleTouchMove)
  document.addEventListener('touchend', handleTouchEnd)
}

function handleKeyDown(e: KeyboardEvent): void {
  if (!props.interactive) return
  const step = e.shiftKey ? 10 : 5
  if (e.key === 'ArrowRight') {
    e.preventDefault()
    emit('seek', Math.min(props.duration, props.currentTime + step))
  } else if (e.key === 'ArrowLeft') {
    e.preventDefault()
    emit('seek', Math.max(0, props.currentTime - step))
  }
}

// Hover tracking via pointer events
function handlePointerEnter() { isHovering.value = true }
function handlePointerLeave() { isHovering.value = false }
function handlePointerMove(e: PointerEvent) {
  hoverPercent.value = getPercentFromEvent(e)
}

import { onMounted, onUnmounted } from 'vue'

onMounted(() => {
  const el = containerRef.value
  if (!el) return
  el.addEventListener('pointerenter', handlePointerEnter)
  el.addEventListener('pointerleave', handlePointerLeave)
  el.addEventListener('pointermove', handlePointerMove as EventListener)
})

onUnmounted(() => {
  const el = containerRef.value
  if (!el) return
  el.removeEventListener('pointerenter', handlePointerEnter)
  el.removeEventListener('pointerleave', handlePointerLeave)
  el.removeEventListener('pointermove', handlePointerMove as EventListener)
})
</script>

<style lang="scss" scoped>
@use '@/styles/design-tokens' as *;

.audio-progress-bar {
  position: relative;
  width: 100%;
  height: 28px;
  display: flex;
  align-items: center;
  cursor: pointer;
  touch-action: none;
  user-select: none;

  @include focus-visible-only;

  &--still {
    cursor: default;
    touch-action: auto;
  }

  &__track {
    position: relative;
    width: 100%;
    height: 8px;
    border-radius: $radius-full;
    background: rgba(var(--v-theme-on-surface), 0.1);
    overflow: hidden;
  }

  &__buffered {
    position: absolute;
    top: 0;
    height: 100%;
    background: rgba(var(--v-theme-on-surface), 0.08);
    border-radius: $radius-full;
    @include transition-fast(width);
  }

  &__fill {
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    border-radius: $radius-full;
    background: var(--audio-accent-color, rgb(var(--v-theme-primary)));
  }

  &__hover {
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    background: rgba(var(--v-theme-on-surface), 0.06);
    border-radius: $radius-full;
    pointer-events: none;
  }

  &__thumb {
    position: absolute;
    top: 50%;
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background: var(--audio-accent-color, rgb(var(--v-theme-primary)));
    transform: translate(-50%, -50%) scale(0);
    @include transition-fast(transform);
    box-shadow: $shadow-sm;
    pointer-events: none;
  }

  &:hover &__thumb,
  &:focus-visible &__thumb {
    transform: translate(-50%, -50%) scale(1);
  }

  // No handle to grab on a bar the listener may not move.
  &--still:hover &__thumb {
    transform: translate(-50%, -50%) scale(0);
  }

  &__tooltip {
    position: absolute;
    bottom: 100%;
    transform: translateX(-50%);
    margin-bottom: 6px;
    padding: 2px 6px;
    border-radius: $radius-sm;
    background: rgba(0, 0, 0, 0.8);
    color: #fff;
    font-size: $text-xs;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
    pointer-events: none;
  }

  @include reduced-motion {
    &__thumb {
      transition: none;
    }
  }
}
</style>
